import { css, keyframes } from 'styled-components';

// Animated border effects for link buttons, ported from the avatar border system.
// All effects are single-color, driven by the CSS variable --ab-color (set by the
// chosen color). Rectangle-safe: conic effects rotate the gradient angle via the
// registered @property --bf-angle (see DesignSiteGlobalStyle) instead of rotating
// the whole element, which would sweep off a wide button.

export const DEFAULT_AB_COLOR = '#ff9000';

const SEPARATOR = ':';

// "rotating-gradient:#ff0000" -> { effect: 'rotating-gradient', color: '#ff0000' }
export const parseAnimation = (value) => {
  if (!value || typeof value !== 'string') return { effect: '', color: '' };
  const idx = value.indexOf(SEPARATOR);
  if (idx === -1) return { effect: value, color: '' };
  return { effect: value.slice(0, idx), color: value.slice(idx + 1) };
};

export const composeAnimation = (effect, color) => {
  if (!effect || effect === 'none') return effect || '';
  return color ? `${effect}${SEPARATOR}${color}` : effect;
};

export const BORDER_EFFECTS = [
  { id: 'borderFlow', label: 'Flow' },
  { id: 'rotating-gradient', label: 'Rotating Gradient' },
  { id: 'pulsing-glow', label: 'Pulsing Glow' },
  { id: 'rainbow-wave', label: 'Color Wave' },
  { id: 'neon-flicker', label: 'Neon Flicker' },
  { id: 'shimmer-sweep', label: 'Shimmer Sweep' },
  { id: 'electric-arc', label: 'Electric Arc' },
  { id: 'comet-trail', label: 'Comet Trail' },
  { id: 'fire-pulse', label: 'Fire Pulse' },
  { id: 'breathing-rings', label: 'Breathing Rings' },
  { id: 'plasma-wave', label: 'Plasma Wave' },
  { id: 'liquid-morph', label: 'Liquid Morph' },
  { id: 'glitch', label: 'Glitch' },
];

const BORDER_EFFECT_IDS = new Set(BORDER_EFFECTS.map((e) => e.id));

export const isBorderEffect = (effect) => BORDER_EFFECT_IDS.has(effect);

// Pseudo-element border-radius per link shape, offset slightly to wrap the button.
export const getButtonBorderShape = (shape) => {
  switch (shape) {
    case 'round':
      return '42px';
    case 'large':
      return '20px';
    case 'medium':
      return '12px';
    case 'small':
      return '8px';
    default:
      return '8px';
  }
};

// --- color shorthands (single chosen color -> derived shades) ---
const C = 'var(--ab-color)';
const light = 'color-mix(in srgb, var(--ab-color) 35%, #ffffff)';
const dim = (p) => `color-mix(in srgb, var(--ab-color) ${p}%, transparent)`;

// --- keyframes ---
const spin = keyframes`to { --bf-angle: 360deg; }`;
const pulse = keyframes`
  0%, 100% { opacity: 0.9; transform: scale(1); }
  50% { opacity: 0.55; transform: scale(1.06); }
`;
const wave = keyframes`
  0% { background-position: 0% 50%; }
  100% { background-position: 200% 50%; }
`;
const flicker = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.75; }
`;
const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;
const firePulse = keyframes`
  0%, 100% { background-position: 0% 50%; filter: blur(2px) brightness(1); }
  50% { background-position: 100% 50%; filter: blur(3px) brightness(1.25); }
`;
const breathe1 = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.18); opacity: 0; }
`;
const breathe2 = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.32); opacity: 0; }
`;
const plasmaPulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.85; }
  50% { transform: scale(1.08); opacity: 1; }
`;
const morph = keyframes`
  0%, 100% { border-radius: var(--border-shape, 8px); }
  33% { border-radius: 40% 60% 55% 45% / 55% 45% 60% 40%; }
  66% { border-radius: 60% 40% 45% 55% / 40% 60% 45% 55%; }
`;
const glitch1 = keyframes`
  0%, 100% { clip-path: inset(0); }
  20% { clip-path: inset(20% 0 55% 0); }
  40% { clip-path: inset(70% 0 5% 0); }
`;
const glitch2 = keyframes`
  0%, 100% { clip-path: inset(0); }
  25% { clip-path: inset(0 0 80% 0); }
  45% { clip-path: inset(45% 0 25% 0); }
`;

const base = css`
  content: '';
  position: absolute;
  border-radius: var(--border-shape, 8px);
  z-index: -1;
  pointer-events: none;
`;

export const BUTTON_BORDER_EFFECTS = {
  'rotating-gradient': css`
    &::before {
      ${base}
      inset: -3px;
      background: conic-gradient(from var(--bf-angle), ${C}, ${light}, ${C});
      animation: ${spin} 3s linear infinite;
    }
  `,

  'pulsing-glow': css`
    &::before {
      ${base}
      inset: -3px;
      background: linear-gradient(45deg, ${C}, ${light});
      filter: blur(1px);
      animation: ${pulse} 2s ease-in-out infinite;
    }
  `,

  'rainbow-wave': css`
    &::before {
      ${base}
      inset: -3px;
      background: linear-gradient(90deg, ${C}, ${light}, ${C}, ${light}, ${C});
      background-size: 200% 200%;
      animation: ${wave} 3s linear infinite;
    }
  `,

  'neon-flicker': css`
    &::before {
      ${base}
      inset: -2px;
      background: ${C};
      box-shadow:
        0 0 8px ${C},
        0 0 16px ${C},
        0 0 28px ${C};
      animation: ${flicker} 0.18s infinite alternate;
    }
  `,

  'shimmer-sweep': css`
    &::before {
      ${base}
      inset: -3px;
      background-color: ${C};
      background-image: linear-gradient(90deg, transparent 0%, transparent 40%, rgba(255, 255, 255, 0.85) 50%, transparent 60%, transparent 100%);
      background-size: 200% 100%;
      animation: ${shimmer} 2s linear infinite;
    }
  `,

  'electric-arc': css`
    &::before {
      ${base}
      inset: -3px;
      background: conic-gradient(from var(--bf-angle), ${C} 0deg 60deg, transparent 60deg 180deg, ${light} 180deg 240deg, transparent 240deg 360deg);
      filter: blur(2px);
      animation: ${spin} 1.5s linear infinite;
    }
  `,

  'comet-trail': css`
    &::before {
      ${base}
      inset: -3px;
      background: conic-gradient(from var(--bf-angle), transparent 0deg 270deg, ${C} 270deg 295deg, ${light} 295deg 315deg, ${dim(50)} 315deg 335deg, transparent 335deg 360deg);
      filter: blur(2px);
      animation: ${spin} 2s linear infinite;
    }
  `,

  'fire-pulse': css`
    &::before {
      ${base}
      inset: -3px;
      background: linear-gradient(90deg, ${C}, color-mix(in srgb, var(--ab-color) 50%, #ffd700), ${C}, color-mix(in srgb, var(--ab-color) 50%, #ff4500), ${C});
      background-size: 300% 100%;
      animation: ${firePulse} 2s ease-in-out infinite;
    }
  `,

  'breathing-rings': css`
    &::before,
    &::after {
      content: '';
      position: absolute;
      inset: -3px;
      border-radius: var(--border-shape, 8px);
      border: 2px solid ${C};
      z-index: -1;
      pointer-events: none;
    }
    &::before {
      animation: ${breathe1} 2s ease-in-out infinite;
    }
    &::after {
      animation: ${breathe2} 2s ease-in-out infinite;
    }
  `,

  'plasma-wave': css`
    &::before {
      ${base}
      inset: -4px;
      background:
        radial-gradient(circle at 30% 40%, ${C} 0%, transparent 55%), radial-gradient(circle at 70% 60%, ${light} 0%, transparent 55%),
        radial-gradient(circle at 50% 50%, ${C} 0%, transparent 70%);
      filter: blur(4px);
      animation: ${plasmaPulse} 4s ease-in-out infinite;
    }
  `,

  'liquid-morph': css`
    &::before {
      ${base}
      inset: -3px;
      background: linear-gradient(45deg, ${C}, ${light});
      filter: blur(1px);
      animation: ${morph} 8s ease-in-out infinite;
    }
  `,

  glitch: css`
    &::before,
    &::after {
      ${base}
      inset: -3px;
      background: linear-gradient(45deg, ${C}, ${light});
    }
    &::before {
      animation: ${glitch1} 0.5s infinite;
    }
    &::after {
      animation: ${glitch2} 0.5s infinite;
    }
  `,
};
